import React, { useRef, useState } from "react";
import { theme } from "../../theme.js";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { icon } from "leaflet";

export default function FileUpload({ requirement, onChange, accept = "application/pdf,image/*", uploadImmediately = false, uploadUrl = "/api/uploads", onUploaded, initialValue = null, existingFile = null, required = false, onExistingDeleted = null }) {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [existingCleared, setExistingCleared] = useState(false);
  // If an initialValue is provided (file already uploaded on a previous visit to this step),
  // restore the displayed state so the user sees the previously uploaded file.
  const [uploadedResponse, setUploadedResponse] = useState(initialValue || null);
  const displayName = selectedFile?.name
    || (uploadedResponse?.filename)
    || (uploadedResponse?.path ? uploadedResponse.path.split(/[\/\\]/).pop() : null)
    || null;

  required= requirement.min_required>0 || required; // treat requirement.required as source of truth for required status, but allow override via props if needed

  // existingFile: a server-saved document { document_path, ... } shown as "currently on file"
  const existingFileName = !existingCleared && (existingFile?.document_path
    ? existingFile.document_path.split(/[\/\\]/).pop()
    : existingFile ? "Fichier existant" : null);

  const isMissing = required && !uploadedResponse && (!existingFile || existingCleared);

  const doUpload = async (file) => {
    if (!file) return null;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(uploadUrl, { method: "POST", body: fd });
      const json = await res.json().catch(() => null);
      setUploadedResponse(json);
      onUploaded && onUploaded(json);
      return json;
    } catch (err) {
      console.error("FileUpload error:", err);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFilePick = async (e) => {
    const f = e.target.files && e.target.files[0];
    setSelectedFile(f || null);
    onChange && onChange(f || null);
    if (uploadImmediately && f) {
      await doUpload(f);
    }
  };

  return (
    <div key={requirement.id} style={{ ...styles.requirementItem, ...(isMissing ? styles.requirementItemMissing : {}) }}>
      <div style={styles.requirementHeader}>
        <span style={styles.requirementTitle}>{requirement.name}</span>
        {required && <span style={styles.requiredStar}>*</span>}
      </div>
      {requirement.description && (
        <p style={styles.requirementDescription}>{requirement.description}</p>
      )}
      <div style={styles.container}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFilePick}
          style={styles.hiddenInput}
        />
        <div style={styles.controls}>
          {!selectedFile && !uploadedResponse && !existingFileName && (
            <button type="button" onClick={() => inputRef.current && inputRef.current.click()} style={styles.pickButton}>
              Choisir un fichier
            </button>
          )}

          {/* Show new upload, OR existing file (styled identically), OR empty state */}
          {(selectedFile || uploadedResponse) ? (
            <div style={styles.fileInfo}>
              <span style={styles.fileName}>{displayName || "Fichier téléchargé"}</span>
              <button type="button" onClick={() => {
                setSelectedFile(null);
                setUploadedResponse(null);
                onChange && onChange(null);
              }} style={styles.removeButton}>
                <XMarkIcon style={styles.icon} />
              </button>
            </div>
          ) : existingFileName ? (
            <div style={styles.fileInfo}>
              <span style={styles.fileName}>{existingFileName}</span>
              <button type="button" onClick={() => {
                setExistingCleared(true);
                onChange && onChange(null);
                onExistingDeleted && onExistingDeleted(existingFile);
              }} style={styles.removeButton}>
                <XMarkIcon style={styles.icon} />
              </button>
            </div>
          ) : (
            <div style={styles.fileInfo}>
              <span style={styles.fileName}>{"Aucun fichier sélectionné"}</span>
            </div>
          )}

          {uploading && <span style={styles.uploading}>Téléversement...</span>}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
    width: "100%",
  },
  requiredStar: {
    color: theme.colors.error || "#e53e3e",
    fontWeight: "bold",
    fontSize: "1.1em",
    marginLeft: "2px",
    marginRight: theme.spacing.xs,
    lineHeight: 1,
  },
  requirementItemMissing: {
    border: `2px solid ${theme.colors.error || "#e53e3e"}`,
    borderRadius: theme.radius.md,
    backgroundColor: "rgba(229,62,62,0.04)",
  },
  hiddenInput: { display: "none" },
  controls: { display: "flex", alignItems: "center", gap: theme.spacing.md },
  pickButton: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: theme.colors.bgDark,
    color: theme.colors.text,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
  },
  fileInfo: { 
    display: "flex", 
    alignItems: "center", 
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.white,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.border}`,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`, 
  },
  fileName: { fontSize: theme.fonts.size.sm, 
    color: theme.colors.textInverted, 
    overflow: "hidden", 
    textOverflow: "ellipsis", 
    whiteSpace: "nowrap", 
    maxWidth: "160px" },
  removeButton: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    background: theme.colors.error,
    color: theme.colors.white,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
  },
  uploading: { 
    fontSize: theme.fonts.size.xs, 
    color: theme.colors.textInverted },
  uploaded: { 
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    background: theme.colors.success,
    color: theme.colors.white,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontSize: theme.fonts.size.xs, 
  },
  
  existingFileIcon: {
    color: "#065f46",
    fontWeight: theme.fonts.weight.bold,
    flexShrink: 0,
  },
  existingBadge: {
    fontSize: theme.fonts.size.xs,
    color: "#065f46",
    backgroundColor: "#d1fae5",
    borderRadius: theme.radius.sm,
    padding: `1px ${theme.spacing.xs}`,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  requirementItem: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.bgLight,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.md,
  },
  requirementHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.md,
    marginBottom: theme.spacing.xs,
  },
  requirementTitle: {
    fontSize: theme.fonts.size.base,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textInverted,
  },
  requirementType: {
    fontSize: theme.fonts.size.xs,
    color: theme.colors.textLight,
    fontStyle: "italic",
  },
  requirementDescription: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    margin: 0,
    lineHeight: "1.5",
  },
  icon: {
    width: "16px",
    height: "16px",
    color: theme.colors.white,
    verticalAlign: "middle",
  },
};
